import { useNavigate, useParams } from 'react-router-dom';
import { useGetPracticeFeedback } from '../../hooks/getPracticeFinish';
import Container from '../../../../core/components/atoms/Container';
import { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import { Toast, toast } from '../../../../core/components/atoms/Toast';
import { IExercise } from '../../../../core/models/exercise';
import ContentLoader from 'react-content-loader';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { useCreatePracticeRemake } from '../../hooks/createPracticeRemake';
import ExamFeedback from '../../../../core/components/templates/Exam/Feedback';
import NoContent from '../../../../core/components/templates/NoContent';
import { useMostraSeuAprovaTrigger } from '../../../../core/components/organism/MostraSeuAprovaModal/useMostraSeuAprovaTrigger';

/** Aproveitamento mínimo, em %, para o convite da campanha #mostraseuaprova. */
const MOSTRA_SEU_APROVA_MIN_SCORE = 50;

export function PracticeFeedback() {
  const { simuladoId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetPracticeFeedback(simuladoId || '');
  const { mutate: createRemake, data: remakeData } = useCreatePracticeRemake();

  const [modifiedExercises, setModifiedExercises] = useState<IExercise[]>([]);

  const { fire: fireMostraSeuAprova } = useMostraSeuAprovaTrigger();
  // O feedback também é aberto ao rever um simulado antigo, e a tela remonta a
  // cada troca de aba. Um disparo por simulado basta.
  const firedSimulationId = useRef<string | null>(null);

  /**
   * Campanha #mostraseuaprova: convida a mostrar o simulado, mas só para quem
   * foi bem nele — abaixo disso o resultado não é o que o aluno quer gravar.
   *
   * Sai junto com a campanha — ver
   * core/components/organism/MostraSeuAprovaModal/README.md.
   */
  useEffect(() => {
    const percentage = data?.score?.percentage;

    if (!simuladoId || typeof percentage !== 'number') return;
    if (percentage <= MOSTRA_SEU_APROVA_MIN_SCORE) return;
    if (firedSimulationId.current === simuladoId) return;

    firedSimulationId.current = simuladoId;

    fireMostraSeuAprova('simulador');
  }, [data, simuladoId, fireMostraSeuAprova]);

  // Função para processar questões de escolha única
  const processSingleAnswer = useCallback((
    it: any,
    alternatives: any[],
    resolution: any[]
  ): IExercise => {
    const exercise = {} as IExercise;
    
    // Encontrar a alternativa correta
    const answer = alternatives.find((option: any) => option.correct === true);
    const itResolution = resolution.find(
      (res: any) => res.alternativeId === answer?.id
    );

    exercise.alternatives = alternatives.map((alt: any) => {
      const idAlt = alt.id;
      
      if (idAlt === itResolution?.myAnswerId) {
        alt.selected = true;

        if (idAlt === itResolution.alternativeId) {
          exercise.correct = true;
          exercise.status = 'correct';
          alt.status = 'success';
        } else {
          exercise.correct = false;
          exercise.status = 'incorrect';
          alt.status = 'error';
        }
      } else {
        alt.selected = false;
        if (idAlt === itResolution?.alternativeId) {
          alt.status = 'correct';
        }
      }

      return alt;
    });

    // Configurar propriedades do exercício
    exercise.id = it._id;
    exercise.exercise_type = it.exercise_type;
    exercise.exerciseType = it.exercise_type;
    exercise.statement = it.statement;
    exercise.resolution = it.resolution;
    exercise.showAnswer = true;
    exercise.multipleAlternatives = false;
    exercise.done = true;
    exercise.ancestors = it.ancestors;
    exercise.hasAlternativeSelected = true;
    exercise.resolution_video = it.resolution_video;
    exercise.subjects = it.subjects;

    return exercise;
  }, []);

  // Função para processar questões de múltipla escolha
  const processMultipleAnswer = useCallback((
    it: any,
    alternatives: any[],
    resolution: any[]
  ): IExercise => {
    const exercise = {} as IExercise;
    
    // Encontrar todas as alternativas corretas
    const correctAnswers = alternatives.filter((option: any) => option.correct === true);
    const correctAnswerIds = correctAnswers.map((answer: any) => answer.id);
    
    // Buscar a resolução correspondente pelos IDs das alternativas corretas
    const itResolution = resolution.find(
      (res: any) => res.isMultipleAnswer && Array.isArray(res.alternativeId) &&
        correctAnswerIds.length === res.alternativeId.length &&
        correctAnswerIds.every((id: string) => res.alternativeId.includes(id))
    );

    // Coletar todos os IDs das respostas do usuário
    let userAnswerIds: string[] = [];

    if (itResolution?.myAnswerId) {
      if (Array.isArray(itResolution.myAnswerId)) {
        userAnswerIds = itResolution.myAnswerId.map((id: any) => String(id));
      } else {
        userAnswerIds = [String(itResolution.myAnswerId)];
      }
    }
    
    // Remover duplicatas
    userAnswerIds = Array.from(new Set(userAnswerIds));
    
    // Processar alternativas
    exercise.alternatives = alternatives.map((alt: any) => {
      const idAlt = String(alt.id).trim();
      const altId = String(alt._id).trim();
      
      // Verificar se foi selecionada
      alt.selected = userAnswerIds.some(userId => {
        const userIdStr = String(userId).trim();
        return userIdStr === idAlt || userIdStr === altId;
      });
      
      // Definir status
      if (alt.selected && correctAnswerIds.includes(alt.id)) {
        alt.status = 'success';
      } else if (alt.selected && !correctAnswerIds.includes(alt.id)) {
        alt.status = 'error';
      } else if (!alt.selected && correctAnswerIds.includes(alt.id)) {
        alt.status = 'error';
      }
      
      return alt;
    });
    
    // Verificar se a questão está correta
    const allCorrectSelected = correctAnswerIds.every((id: string) => 
      userAnswerIds.some(userId => String(userId).trim() === String(id).trim())
    );
    const noIncorrectSelected = userAnswerIds.every((id: string) =>
      correctAnswerIds.some(correctId => String(correctId).trim() === String(id).trim())
    );
    
    exercise.correct = allCorrectSelected && noIncorrectSelected;
    exercise.status = exercise.correct ? 'correct' : 'incorrect';
    
    // Configurar propriedades do exercício
    exercise.id = it._id;
    exercise.exercise_type = it.exercise_type;
    exercise.exerciseType = it.exercise_type;
    exercise.statement = it.statement;
    exercise.resolution = it.resolution;
    exercise.showAnswer = true;
    exercise.multipleAlternatives = true;
    exercise.done = true;
    exercise.ancestors = it.ancestors;
    exercise.hasAlternativeSelected = userAnswerIds.length > 0;
    exercise.resolution_video = it.resolution_video;
    exercise.subjects = it.subjects;

    return exercise;
  }, []);

  //TODO: Transformar em hook junto com o do Running
  useEffect(() => {
    if (data) {
      const resolution = data.resolutions;
      const exercises = data.exercises;

      const updatedExercises = exercises.map((it: any) => {
        // Preparar alternativas
        const alternatives = it.alternatives.map((alt: any) => ({
          ...alt,
          id: alt._id,
        }));

        // Processar com base no tipo de exercício
        if (it.exercise_type === 'multiple_answer') {
          return processMultipleAnswer(it, alternatives, resolution);
        } else {
          return processSingleAnswer(it, alternatives, resolution);
        }
      });

      setModifiedExercises(updatedExercises);
    }
  }, [data, processSingleAnswer, processMultipleAnswer]);

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        message: 'Não foi possivel recuperar seu resultado',
        title: 'Erro!',
      });
    }
  }, [isError]);

  useEffect(() => {
    if (remakeData && remakeData.simulatedExamId) {
      navigate(`/simulados/simulado/${remakeData.simulatedExamId}`);
    }
  }, [remakeData]);

  if(isError) {
    const url = `/simulados/provas`;

    return <NoContent url={url} />
  }

  return (
    <Container gap='xxl' direction='col' className='xs:mb-[30px] xl:mb-[30px]'>
      <Breadcrumbs
        items={[
          {
            label: 'Simulador de provas',
            href: `/simulados/provas`,
          },
          {
            label: 'Criar simulado',
            href: `/simulados/criar`,
          },
          {
            label: data?.title + ': Gabarito' || 'Simulado: Gabarito',
            href: '#',
          },
        ]}
      />
      {isLoading || !data ? (
        <Fragment>
          <ContentLoader
            backgroundColor={'#a7adba'}
            foregroundColor={'#c0c5ce'}
            viewBox='0 0 500 100'
          >
            <rect x='0' y='10' rx='2' ry='2' width='700' height='300' />
          </ContentLoader>
          {Array.from({ length: 10 }, (_, index) => (
            <ContentLoader
              key={index}
              backgroundColor={'#a7adba'}
              foregroundColor={'#c0c5ce'}
              viewBox='0 0 500 35'
            >
              <rect x='0' y='10' rx='2' ry='2' width='300' height='13' />
              <rect x='0' y='25' rx='2' ry='2' width='250' height='10' />
            </ContentLoader>
          ))}
        </Fragment>
      ) : (
        <div className='p-2'>
          <ExamFeedback
            essay={data.essay.id ? data.essay : undefined}
            exercises={modifiedExercises}
            correctAnswersPercentage={data.score.percentage}
            correctAnswersTotal={data.score.totalCorrectAnswers}
            wrongAnswersTotal={
              Number(data.score.totalQuestions) -
              Number(data.score.totalCorrectAnswers)
            }
            timeSpent={data.timeSpent || null}
            isLoading={isLoading}
            onRemake={() => createRemake(simuladoId || '')}
            onRevision={() => navigate('/simulados/reviews')}
            isPractice={true}
          />
        </div>
      )}
      {isError && <Toast />}
    </Container>
  );
}
